import { CSSProperties, useEffect, useRef, useState } from 'react';
import { Button, Icon } from '@/components/ui';
import useNavigate from '@/hooks/use-navigate';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { cn } from '@/utils';
import { resolveHeroGradient } from '../hero-section/hero-gradient-presets';
import BackgroundCarousel, {
  BackgroundCarouselHandle,
  CarouselDots,
} from './background-carousel/background-carousel';

type TextStyle = {
  text: string;
  color: string;
  fontSize: string;
  fontWeight: string;
};

type FullBleedHeroSectionProps = {
  label?: TextStyle;
  title: TextStyle;
  subtitle: TextStyle;
  body?: TextStyle;
  button: {
    text: string;
    href: string;
    startIcon: string;
    endIcon: string;
  };
  images: string[];
  overlayGradient?: string;
  minHeight?: number;
  contentPaddingStyle?: CSSProperties;
  headingLevel?: 1 | 2;
};

const fontWeightClass = (fontWeight: string) =>
  cn({
    'font-light': fontWeight === 'light',
    'font-normal': fontWeight === 'normal',
    'font-semibold': fontWeight === 'semi-bold',
    'font-bold': fontWeight === 'bold',
  });

const FullBleedHeroSection = ({
  label,
  title,
  subtitle,
  body,
  button,
  images,
  overlayGradient,
  minHeight = 640,
  contentPaddingStyle,
  headingLevel = 1,
}: FullBleedHeroSectionProps) => {
  const resolveThemeColor = useResolveThemeColor();
  const navigate = useNavigate();
  const carouselRef = useRef<BackgroundCarouselHandle | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const resolvedGradient = resolveHeroGradient(overlayGradient);

  useEffect(() => {
    navigate.prefetch(button.href || '/');
  }, [navigate, button.href]);

  const TitleTag = headingLevel === 1 ? 'h1' : 'h2';

  return (
    <div
      className="full-bleed-hero-section relative w-full overflow-hidden"
      style={{ '--full-bleed-min-h-px': minHeight } as CSSProperties}
    >
      <BackgroundCarousel
        ref={carouselRef}
        images={images}
        onSelect={index => setSelectedIndex(index)}
      />

      {resolvedGradient ? (
        <div
          className="pointer-events-none absolute inset-0 z-1"
          style={{ backgroundImage: resolvedGradient }}
        />
      ) : null}

      <div className="relative z-10 flex min-h-[inherit] items-center" style={contentPaddingStyle}>
        <div className="container mx-auto w-full px-5 @lg:px-8">
          <div className="flex max-w-xl flex-col gap-3 @lg:gap-4">
            {label?.text ? (
              <span
                className={cn(
                  'text-xs tracking-[0.2em] uppercase @lg:text-sm',
                  fontWeightClass(label.fontWeight),
                )}
                style={{
                  color: resolveThemeColor(label.color),
                  fontSize: label.fontSize,
                }}
              >
                {label.text}
              </span>
            ) : null}
            <TitleTag
              className={cn(
                'text-3xl leading-tight uppercase @md:text-4xl @lg:text-5xl',
                fontWeightClass(title.fontWeight),
              )}
              style={{ color: resolveThemeColor(title.color), fontSize: title.fontSize }}
            >
              {title.text}
            </TitleTag>
            {subtitle.text ? (
              <p
                className={cn('text-lg @lg:text-xl', fontWeightClass(subtitle.fontWeight))}
                style={{
                  color: resolveThemeColor(subtitle.color),
                  fontSize: subtitle.fontSize,
                }}
              >
                {subtitle.text}
              </p>
            ) : null}
            {body?.text ? (
              <p
                className={cn(
                  'max-w-md text-sm leading-relaxed @lg:text-base',
                  fontWeightClass(body.fontWeight),
                )}
                style={{
                  color: resolveThemeColor(body.color),
                  fontSize: body.fontSize,
                }}
              >
                {body.text}
              </p>
            ) : null}
            {button.text ? (
              <div className="mb-6 pt-2">
                <Button
                  variant="bordered"
                  className="border-foreground bg-content1 text-foreground min-w-[140px] font-medium uppercase"
                  onPress={() => navigate(button.href || '/')}
                  onMouseEnter={() => navigate.prefetch(button.href || '/')}
                  onFocus={() => navigate.prefetch(button.href || '/')}
                  startContent={button.startIcon ? <Icon icon={button.startIcon} /> : undefined}
                  endContent={button.endIcon ? <Icon icon={button.endIcon} /> : undefined}
                >
                  {button.text}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <CarouselDots
        count={images.length}
        selectedIndex={selectedIndex}
        onSelect={index => carouselRef.current?.scrollTo(index)}
        className="absolute right-0 bottom-6 left-0 z-20"
      />
    </div>
  );
};

export default FullBleedHeroSection;
